import { Injectable, EventEmitter, Inject, Optional } from '@angular/core';
import {
    Observable,
    BehaviorSubject,
    pipe,
    forkJoin,
    of,
    combineLatest,
    zip
} from 'rxjs';
import {
    map,
    finalize,
    first,
    tap,
    takeWhile,
    take,
    catchError,
    filter,
    mergeMap
} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { LgvModel } from '../../data-models/longevity.model';
import { environment as env } from '../../../environments/environment';
import { DatePipe } from '@angular/common';
import { LgvAuthMgrService } from './lgv-auth-mgr.service';
import { NotificationService } from '../core.module';
import { LgvTSHelper } from '../utility/ts-helper';
import { ProviderType } from '../utility/lgv-constants';
import { LgvConstants } from '../utility/lgv-constants';
import { LgvCompanyService } from './lgv-company.service';
import {
    BusinessTypeId,
    BusinessTypeName
} from '../../modules/default/default.enum';
import { LgvLocalStorageService } from './lgv-local-storage.service';
import { LgvLoanApp } from '../../data-models/lgv-loan-application.model';
import { isatty } from 'tty';
import { LgvEmailModel } from '../../data-models/lgv-email-model';
import { StdObj } from '../../data-models/standard-objects';
import { Router } from '@angular/router';
import { LicensingPipeline } from '../../data-models/licensing.model';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { SplashDialogComponent } from '../../shared/splash-dialog/splash-dialog.component';

@Injectable({
    providedIn: 'root'
})
export class LgvUserService {
    [x: string]: any;

    //#region DBUser

    /* DBUser is always the physical user logged in via Auth0, unless actual Client in ClientView - Then Client Agent */

    _roles: LgvModel.Role[] = [];
    roles$ = new BehaviorSubject(this._roles);

    nfmCompanyCodes: string[] = ['nfm', 'mshl', 'element', 'bluprint'];

    _DBUserSS: LgvModel.AgentSS = null;
    DBUserSS$ = new BehaviorSubject<LgvModel.AgentSS>(null);
    DBUserInfo$ = this.DBUserSS$.pipe(
        map(x => {
            return x === null ? null : x.agentInfo;
        })
    ); // Phasing DBUserInfo$ to DBUserSS$

    DBUserRoles$ = this.DBUserSS$.pipe(
        first(f => f !== null),
        map(ss => ss.roles)
    );
    IsAgent$ = this.DBUserRoles$.pipe(
        map(arr => {
            return !this.authMgr.IsClient && arr.indexOf('Agent') !== -1;
        })
    );
    IsAdmin$ = this.DBUserRoles$.pipe(
        map(arr => {
            return !this.authMgr.IsClient && arr.indexOf('Admin') !== -1;
        })
    );
    IsCompanyAdmin$ = this.DBUserRoles$.pipe(
        map(arr => {
            return arr.indexOf('CompanyAdmin') !== -1;
        })
    );
    IsProcessor$ = this.DBUserRoles$.pipe(
        map(arr => {
            return arr.indexOf('Processor') !== -1;
        })
    );
    IsDLR$ = this.DBUserRoles$.pipe(
        map(arr => {
            return arr.indexOf('DualLicensedAgent') !== -1;
        })
    );
    IsCK$ = this.DBUserRoles$.pipe(
        map(arr => {
            return arr.indexOf('ConciergeKey') !== -1;
        })
    );
    IsPreLicensedAgent$ = this.DBUserRoles$.pipe(
        map(arr => {
            return arr.indexOf('PreLicensedAgent') !== -1;
        })
    );
    UserHasMailBox$ = this.DBUserSS$.pipe(
        first(f => !!f),
        map(ss => !!ss.agentInfo.username)
    );
    IsAnyAdmin$ = this.DBUserRoles$.pipe(
        first(f => f !== null),
        map(arr => {
            return (
                arr.indexOf('Admin') !== -1 ||
                arr.indexOf('CompanyAdmin') !== -1
            );
        })
    );
    DBUserCompany$ = this.DBUserSS$.pipe(
        first(f => f !== null),
        map(ss => ss.company)
    );
    isOnboarding$ = new BehaviorSubject(null);
    IsOnlyAgent$: Observable<boolean> = this.DBUserRoles$.pipe(
        map(roles => roles.length === 1 && roles[0] === 'Agent')
    );
    get DBUserRoles(): string[] {
        return this.DBUserSS$.value === null ? [] : this.DBUserSS$.value.roles;
    }
    get HaveDBUserSS(): boolean {
        return (
            this.DBUserSS$.value !== null &&
            this.DBUserSS$.value.agentInfo !== null
        );
    }
    get IsAgentAssistant(): boolean {
        return (
            this.HaveDBUserSS &&
            this.DBUserRoles &&
            this.DBUserRoles.indexOf('Assistant') !== -1
        );
    }
    get IsAgent(): boolean {
        return (
            !this.authMgr.IsClient &&
            this.HaveDBUserSS &&
            this.DBUserRoles &&
            this.DBUserRoles.indexOf('Agent') !== -1
        );
    }
    get IsAdmin(): boolean {
        return (
            !this.authMgr.IsClient &&
            this.HaveDBUserSS &&
            this.DBUserRoles &&
            this.DBUserRoles.indexOf('Admin') !== -1
        );
    }
    get IsCompanyAdmin(): boolean {
        return (
            this.HaveDBUserSS &&
            this.DBUserRoles &&
            this.DBUserRoles.indexOf('CompanyAdmin') !== -1
        );
    }
    get IsAssistant(): boolean {
        return (
            this.HaveDBUserSS &&
            this.DBUserRoles &&
            this.DBUserRoles.indexOf('Assistant') !== -1
        );
    }
    get IsProcessor(): boolean {
        return (
            this.HaveDBUserSS &&
            this.DBUserRoles &&
            this.DBUserRoles.indexOf('Processor') !== -1
        );
    }
    get IsAnyAdmin(): boolean {
        return this.IsAdmin || this.IsCompanyAdmin;
    }
    get IsAgentOrAssistant(): boolean {
        return this.IsAgent || this.IsAgentAssistant;
    }
    get IsDLR(): boolean {
        return this.DBUserRoles?.indexOf('DualLicensedAgent') !== -1;
    }
    get IsCK(): boolean {
        return this.DBUserRoles?.indexOf('ConciergeKey') !== -1;
    }
    get isPreLicensedAgent(): boolean {
        return this.DBUserRoles?.indexOf('PreLicensedAgent') !== -1;
    }

    get defaultUserHomePage$(): Observable<string> {
        return of('/admin');
    }

    //#endregion DBUser Stuff

    //#region Agent

    _AgentSS: LgvModel.AgentSS = null;
    AgentSS$ = new BehaviorSubject(this._AgentSS);
    _PrevCoBrandedAgentDTO: LgvModel.AgentDTO = null;
    PrevCoBrandedAgentDTO$ = new BehaviorSubject(this._PrevCoBrandedAgentDTO);

    _agentAssets: LgvModel.BrandingAsset[] = null;
    agentAssets$ = new BehaviorSubject(this._agentAssets);
    get profileImageAsset$(): Observable<string> {
        return this.getThisBrandingAsset$('User_Profile');
    }

    get Agent$(): Observable<LgvModel.Agent> {
        return this.AgentSS$.pipe(
            map(x => {
                return x === null ? null : x.agentInfo;
            })
        );
    } // Phasing Agent$ to AgentSS$
    get AgentID(): string {
        return !this.IsAgentReady$ ? null : this.AgentSS$.value?.agentInfo?.id;
    }
    get Company$(): Observable<LgvModel.CompanyInfo> {
        return this.AgentSS$.pipe(
            map(x => {
                return x === null ? null : x.company;
            })
        );
    }
    CheckAgentRoleAdmin$ = this.AgentSS$.pipe(
        map(ss => {
            return ss === null ? false : ss.roles.indexOf('Admin') !== -1;
        })
    );
    AgentRoles$ = this.AgentSS$.pipe(
        first(f => f !== null),
        map(ss => ss.roles)
    );

    get IsCoBranding$(): Observable<boolean> {
        return this.AgentSS$.pipe(
            first(f => f !== null),
            map(x => {
                return x.coBrandedAgent !== null;
            })
        );
    }
    get isREAgent$(): Observable<boolean> {
        return this.Company$.pipe(
            first(f => f !== null),
            map(x => {
                return (
                    !!x &&
                    (x.businessTypeName === 'Real Estate' ||
                        LgvConstants.isRealEstateBusinessType(x.businessTypeId))
                );
            })
        );
    }
    get AgentDTO$(): Observable<LgvModel.AgentDTO> {
        return this.AgentSS$.pipe(
            first(f => f !== null),
            map(x => {
                return LgvUserService.ConvertAgentDTO(x);
            })
        );
    }
    get CoBrandedAgentDTO$(): Observable<LgvModel.AgentDTO> {
        return this.AgentSS$.pipe(
            first(f => f !== null),
            map(x => {
                return LgvUserService.ConvertCobrandedAgentDTO(x);
            })
        );
    }

    get IsReadyForPricing$(): Observable<boolean> {
        return this.AgentSS$.pipe(
            first(f => f !== null),
            map(x => {
                return x.coBrandedAgent === null
                    ? x.agentInfo.isReadyForPricing
                    : x.coBrandedAgent.agentInfo.isReadyForPricing;
            })
        );
    }

    get AgentExcludedPages_Original$(): Observable<string[]> {
        return this.AgentSS$.pipe(
            first(f => f !== null),
            map(x => {
                return x.settings.pages
                    .filter(p => !p.isVisible)
                    .map(page => page.key);
            })
        );
    }
    get AgentIsHidingPricingInUserSettings$(): Observable<boolean> {
        return this.AgentSS$.pipe(
            first(f => f !== null),
            map(x => {
                return x?.settings?.hidePricingForClientView;
            })
        );
    }

    get AgentIsExcludingRefinanceOptions$(): Observable<boolean> {
        // agent can currently exlcude in 2 ways.  User setting and excluded page
        return (
            this.AgentIsHidingPricingInUserSettings$ ||
            this.AgentExcludedPages_Original$.pipe(
                map(arr => {
                    return arr.indexOf('opportunities') !== -1;
                })
            )
        );
    }

    // This assumes that a co-branded agent object is only there for Real Estate agents.  If RE agent, and have a co-branded agent obj, then return that agent for pricing
    get PricingAgentDTO$(): Observable<LgvModel.AgentDTO> {
        return this.AgentSS$.pipe(
            filter(f => f !== null),
            map(x => {
                return x.coBrandedAgent
                    ? LgvUserService.ConvertCobrandedAgentDTO(x)
                    : LgvUserService.ConvertAgentDTO(x);
            })
        );
    }
    //get PricingAgentCompany$(): Observable<string> { return this.PricingAgentDTO$.pipe(first(f => f !== null), map(x => { return x.company && x.company.name ? x.company.name : "" })) }

    _PricingAgentCompany: LgvModel.CompanyInfo = null;
    PricingAgentCompany$ = new BehaviorSubject(this._PricingAgentCompany);

    _loanApplicationCompany: LgvModel.ApplicationCompany = null;
    loanApplicationCompany$ = new BehaviorSubject(this._loanApplicationCompany);

    _pricingApplicationCompany: LgvModel.ApplicationCompany = null;
    pricingApplicationCompany$ = new BehaviorSubject(
        this._pricingApplicationCompany
    );

    get havePricingCompany$(): Observable<boolean> {
        return of(!!this.pricingApplicationCompany$.value);
    }
    get isOB$(): Observable<boolean> {
        return this.pricingApplicationCompany$.pipe(
            first(f => f !== null),
            map(x => {
                return x.name === 'OptimalBlue';
            })
        );
    }
    get isLP$(): Observable<boolean> {
        return this.pricingApplicationCompany$.pipe(
            first(f => f !== null),
            map(x => {
                return x.name === 'LenderPrice';
            })
        );
    }

    public ApplyNowUrl(email: string, company: LgvModel.CompanyInfo): string {
        let bldUrl = `${company.applyNowBaseUrl}${email}?cid=HomeDashboardApp_${company.companyCode}`;
        return bldUrl;
    }

    get ApplyNowUrl$(): Observable<string> {
        if (!!this.PricingAgentCompany$.value) {
            return this.PricingAgentDTO$.pipe(
                map(x => {
                    return x === null
                        ? null
                        : this.ApplyNowUrl(
                              x.agentInfo.email,
                              this.PricingAgentCompany$.value
                          );
                })
            );
        } else {
            this.NotificationSvc.error('Pricing company not set up');
            return of(null);
        }
    }

    get loanAppCompany$(): Observable<LgvModel.ApplicationCompany> {
        return this.PricingAgentCompany$.pipe(
            first(f => !!f),
            map(x => {
                return x.applicationCompanies.find(
                    ac => ac.type === 'LoanApplications'
                );
            })
        );
    }

    get readyToApply$(): Observable<boolean> {
        // no apply now button for now...
        return of(false);

        // return combineLatest(this.PricingAgentDTO$, this.loanAppCompany$).pipe(
        //     filter(([pAgent, appCompany]) => !!pAgent), map(([pAgent, appCompany]) => {
        //         let haveAppUrl = !!pAgent?.agentInfo.applicationUrl;
        //         let isAPIApplication = LgvUserService.isApplicationCompany(appCompany);
        //         let isLendingPad = appCompany.name.toUpperCase() === 'LENDINGPAD';
        //         return (!!isAPIApplication || !!haveAppUrl) && !isLendingPad;
        //     }));
    }

    get CoBrandingRealEstateCompany$(): Observable<LgvModel.Agent_Basic[]> {
        return this.CobrandedRealEstatePartners$.pipe(
            map(x => {
                var filterArray = !x
                    ? null
                    : x.reduce((accumalator, current) => {
                          if (
                              !accumalator.some(
                                  item => item.id === current.company.id
                              )
                          ) {
                              const _x = {
                                  id: current.company.id,
                                  name: current.company.name,
                                  companyCode: current.company.companyCode
                              };
                              accumalator.push(_x);
                          }
                          return accumalator;
                      }, []);
                return !x ? null : filterArray;
            })
        );
    }

    _AgentState: string = 'Loading';
    AgentState$ = new BehaviorSubject(this._AgentState);
    get IsAgentLoading$(): Observable<boolean> {
        return this.AgentState$.pipe(map(x => x === 'Loading'));
    }
    get IsAgentError$(): Observable<boolean> {
        return this.AgentState$.pipe(map(x => x === 'Error'));
    }
    get IsAgentReady$(): Observable<boolean> {
        return this.AgentState$.pipe(map(x => x === 'Ready'));
    }
    AgentClientPages$ = this.AgentSS$.pipe(
        first(f => f !== null),
        map(ss => ss.settings.pages)
    );

    _ExcludedPagesForAgent: string[] = null;
    ExcludedPagesForAgent$ = new BehaviorSubject(this._ExcludedPagesForAgent);

    //#endregion Agent

    //#region Other Properties

    //_ClientPages: LgvModel.Page[]=null;
    //ClientPages$ = new BehaviorSubject(this._ClientPages);

    IsLoading: boolean = true;

    _userList: LgvModel.UserDTO[] = [];
    userList$ = new BehaviorSubject(this._userList);

    AssistantImpersonationKey = 'Lgv_AssistantImpersonation';
    get SavedAssistantImpersonation(): string {
        return localStorage.getItem(this.AssistantImpersonationKey);
    }
    set SavedAssistantImpersonation(x: string) {
        if (x === null) {
            localStorage.removeItem(this.AssistantImpersonationKey);
        } else {
            localStorage.setItem(this.AssistantImpersonationKey, x);
        }
    }

    _DBUserAssistingAgents: LgvModel.Assistant[] = null;
    DBUserAssistingAgents$ = new BehaviorSubject(this._DBUserAssistingAgents);

    _DBUserAssistants: LgvModel.Assistant[] = null;
    DBUserAssistants$ = new BehaviorSubject(this._DBUserAssistants);

    _CobrandedRealEstatePartners: LgvModel.AgentDTO[] = null;
    CobrandedRealEstatePartners$ = new BehaviorSubject(
        this._CobrandedRealEstatePartners
    );

    get IsImpersonatingAgent(): boolean {
        return (
            !this.authMgr.IsClient &&
            this.DBUserSS$.value !== null &&
            this.AgentSS$.value &&
            this.AgentSS$.value.agentInfo !== null &&
            this.DBUserSS$.value.agentInfo?.id.toUpperCase() !==
                this.AgentSS$.value.agentInfo?.id.toUpperCase()
        );
    }

    get IsImpersonatingAgent$(): Observable<boolean> {
        return combineLatest([this.DBUserSS$, this.AgentSS$]).pipe(
            //tap(t1 => { console.log('t1:'); console.log(t1); }),
            map(([dbUserSS, agentSS]) => {
                return (
                    dbUserSS &&
                    agentSS &&
                    dbUserSS.agentInfo &&
                    agentSS.agentInfo &&
                    dbUserSS.agentInfo.id !== agentSS.agentInfo.id
                );
            })
        );
    }

    //#endregion Other Properties

    LoanPurposeType$ = new BehaviorSubject([]);
    LoanType$ = new BehaviorSubject([]);
    LienType$ = new BehaviorSubject([]);

    //#region Emails
    numUnreadEmails$ = new BehaviorSubject(-1);
    onNewEmail: EventEmitter<any> = new EventEmitter();
    folderSummary$ = new BehaviorSubject(null as LgvEmailModel.FolderSummary);

    get agentHasMailbox() {
        return (
            this.authMgr.IsAuth0 &&
            !!this.AgentSS$.value?.agentInfo?.username &&
            !this.isPreLicensedAgent
        );
    }

    private initializeEmailService() {
        if (this.agentHasMailbox) {
            this.startManuallyCheckingEmailStatus();
        } else {
            console.log('UserEmailService: User does not have a mailbox');
        }
    }

    private startManuallyCheckingEmailStatus() {
        // initial search right away
        this.getFolderSummary();

        // search every 5 min. This is just a "fall back" incase the push notifications for new emails doesn't work
        setInterval(() => {
            this.getFolderSummary();
        }, 60 * 5 * 1000);
    }

    public getFolderSummary() {
        if (!this.AgentSS$.value?.agentInfo?.id) return;

        this.getUserEmailSummary(this.AgentSS$.value.agentInfo.id)
            .pipe(first())
            .subscribe(
                summary => {
                    this.folderSummary$.next(summary);

                    let inbox = summary.folders.find(
                        x => x.displayName === 'Inbox'
                    );
                    if (inbox) {
                        this.numUnreadEmails$.next(inbox.unreadEmailCount);
                    }
                },
                (err: Error) => {
                    const msg = 'There was an error fetching email summary';
                    console.log(msg);
                }
            );
    }

    public onGoToEmail() {
        if (this.router.url === '/admin/view-email') {
            window.location.reload();
        } else {
            let url = `/#/admin/view-email`;
            window.open(url, '_blank');
        }
    }

    public getUserEmailSummary(
        userId: string
    ): Observable<LgvEmailModel.FolderSummary> {
        let bldUrl = `${env.LgvApiBaseUrl}/users/${userId}/emails/summary`;
        return this.http.get<LgvEmailModel.FolderSummary>(bldUrl).pipe(first());
    }

    public getUserEmails(
        userId: string,
        folder: string,
        searchText: string = null,
        filtryByUnread: boolean = false,
        orderBy: string = null,
        clientList: LgvModel.ClientInfo[] = [],
        paginationInfo: StdObj.PaginationInfo = null
    ): Observable<LgvEmailModel.EmailSearchSummary> {
        let bldUrl = `${env.LgvApiBaseUrl}/users/${userId}/emails`;

        bldUrl += `?folder=${folder}`;

        // Note the * appended to the end.  Requested by Raffy for Azure AI search.
        if (searchText && searchText.length > 0) {
            bldUrl += `&searchText=${searchText}*`;
        }

        if (filtryByUnread) {
            bldUrl += `&filter=isRead eq false`;
        }

        if (orderBy) {
            bldUrl += `&orderBy=${orderBy}`;
        }

        if (clientList?.length > 0) {
            for (let client of clientList) {
                bldUrl += `&filter=clients/any(c: c/id eq '${client.id}')`;
            }
        }

        if (paginationInfo) {
            bldUrl += paginationInfo.oDataSuffix;
        }

        return this.http
            .get<LgvEmailModel.EmailSearchSummary>(bldUrl)
            .pipe(first());
    }

    public getUserEmailDetails(
        userId: string,
        emailId: string
    ): Observable<LgvEmailModel.Email> {
        let bldUrl = `${env.LgvApiBaseUrl}/users/${userId}/emails/${emailId}`;
        return this.http.get<LgvEmailModel.Email>(bldUrl).pipe(first());
    }

    public updateUserEmail(
        userId: string,
        email: LgvEmailModel.Email
    ): Observable<LgvEmailModel.Email> {
        let bldUrl = `${env.LgvApiBaseUrl}/users/${userId}/emails/${email.id}`;
        return this.http
            .patch<LgvEmailModel.Email>(bldUrl, email)
            .pipe(first());
    }

    public sendUserEmail(userId: string, formData: FormData): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/users/${userId}/emails/send`;
        return this.http.post<any>(bldUrl, formData).pipe(first());
    }

    public saveDraftEmail(userId: string, formData: FormData): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/users/${userId}/emails/draft`;
        return this.http.put<any>(bldUrl, formData).pipe(first());
    }

    //#endregion Emails

    constructor(
        private http: HttpClient,
        private lgvCompanySvc: LgvCompanyService,
        private authMgr: LgvAuthMgrService,
        private lgvLSSvc: LgvLocalStorageService,
        private NotificationSvc: NotificationService,
        private datePipe: DatePipe,
        private router: Router,
        private matDialog: MatDialog
    ) {
        // Initialize DBUserSS$ as soon as possible
        this.authMgr.OnLogOutEvent.subscribe(() => {
            this.ClearUserList();
            this.DBUserSS$.next(null);
        });

        // Initialize email service when agent info is available
        this.AgentSS$.pipe(filter(f => !!f)).subscribe(() => {
            this.initializeEmailService();
        });

        // check for saved impersonation and restore
        let savedImpersonation = this.SavedAssistantImpersonation;
        if (
            savedImpersonation &&
            savedImpersonation !== undefined &&
            savedImpersonation.length > 0
        ) {
            // Make sure they still have access to user
            this.DBUserAssistingAgents$.pipe(
                first(x => x !== null && x.length > 0)
            ).subscribe(list => {
                if (list.findIndex(x => x.id !== savedImpersonation) !== -1) {
                    this.ImpersonateThisAgentAsAssistant(savedImpersonation);
                }
            });
        }

        this.initLookups();
    }

    public initLookups() {
        if (this.LoanPurposeType$.value?.length === 0) {
            this.LoadLoanPurposeType()
                .pipe(take(1))
                .subscribe(res => {
                    this.LoanPurposeType$.next(res);
                });
        }
        if (this.LoanType$.value?.length === 0) {
            this.LoadLoanType()
                .pipe(take(1))
                .subscribe(res => {
                    this.LoanType$.next(res);
                });
        }
        if (this.LienType$.value?.length === 0) {
            this.LoadLienType()
                .pipe(take(1))
                .subscribe(res => {
                    this.LienType$.next(res);
                });
        }
    }

    public LoadUsers() {
        this.IsLoading = true;
        let bldUrl = `${env.LgvApiBaseUrl}/users?$top=5000`;
        this.http
            .get<any>(bldUrl)
            .pipe(first())
            .subscribe(
                x => {
                    let tmpUsers = [];
                    for (let assistant of x.value) {
                        tmpUsers.push(
                            LgvUserService.ConvertObjToUserDTO(assistant)
                        );
                    }
                    this.userList$.next(tmpUsers);

                    this.IsLoading = false;
                },
                () => {
                    this.IsLoading = false;
                }
            );
    }

    public ClearUserList() {
        this.DBUserSS$.next(null);
        this.userList$.next(null);
    }

    public SearchCachedUsers(
        searchText: string
    ): Observable<LgvModel.UserDTO[]> {
        // return empty array if nothing to search on
        if (
            searchText === null ||
            searchText === '' ||
            typeof searchText === 'object'
        ) {
            return of([]);
        }

        searchText = searchText.toLowerCase();

        return this.userList$.pipe(
            map(arr => {
                return arr.filter(
                    x =>
                        x.firstName.toLowerCase().indexOf(searchText) !== -1 ||
                        x.lastName.toLowerCase().indexOf(searchText) !== -1
                );
            })
            //,tap(t => { console.log('SearchCachedUsers:'); console.log(t); })
        );
    }

    public CachedUsersByRole(role: string): Observable<LgvModel.UserDTO[]> {
        // return empty array if nothing to search on
        if (role === null || role === '' || typeof role === 'object') {
            return of([]);
        }

        return this.userList$.pipe(
            map(arr => {
                return arr.filter(x => x.userRoles.includes(role));
            })
        );
    }

    public SearchWebServiceUsers(
        searchText: string,
        userType: string = null
    ): Observable<any> {
        let param = userType != null ? `&userType=${userType}` : '';
        let bldUrl = `${env.LgvApiBaseUrl}/users?txtSearch=${searchText}${param}`;
        return this.http.get<any>(bldUrl).pipe(first());
    }

    private SetAgentSS(
        agentSS: LgvModel.AgentSS,
        isPublicZone: boolean = false
    ) {
        //console.log('+_+_+_+_+_+_ Setting AgentSS$ _+_+_+_+_+_+');
        //console.log(agentSS);

        let isNewAgent =
            !agentSS ||
            !this.AgentSS$.value ||
            this.AgentSS$.value.agentInfo.id !== agentSS.agentInfo.id;
        if (isNewAgent) {
            this.CobrandedRealEstatePartners$.next(null);
            this.agentAssets$.next(null);
            this.PricingAgentCompany$.next(null);
        }

        let excludedPages: string[] = [];
        try {
            if (agentSS.settings && agentSS.settings.pages) {
                excludedPages = agentSS.settings.pages
                    .filter(p => !p.isVisible)
                    .map(page => page.key);
            }
        } catch (e) {
            console.log('Error getting excluded page:');
            console.log(e);
        }
        this.ExcludedPagesForAgent$.next(excludedPages);

        this.AgentSS$.next(agentSS);
        this.AgentState$.next('Ready');
        if (agentSS && isNewAgent && !this.authMgr.IsClient && !isPublicZone) {
            this.LoadDBUserCobrandingRealEstatePartners(agentSS.agentInfo.id);
            this.loadUserAssets(agentSS.agentInfo.id);
        }

        let pricingCompanyId = !!agentSS?.coBrandedAgent
            ? agentSS.coBrandedAgent.company.id
            : agentSS.company.id;
        this.lgvCompanySvc
            .getPublicCompanyInfoById(pricingCompanyId)
            .pipe(first())
            .subscribe(pricingCompanyResponse => {
                // kxk: todo - Test w/ co-branding agent (as agent and as client)
                //console.log('=========== Have Pricing Company ===========');
                //console.log(pricingCompanyResponse);
                this.loanApplicationCompany$.next(
                    pricingCompanyResponse.applicationCompanies.find(
                        ac => ac.type === 'LoanApplications'
                    )
                );
                this.pricingApplicationCompany$.next(
                    pricingCompanyResponse.applicationCompanies.find(
                        ac => ac.type === 'Pricing'
                    )
                );
                this.PricingAgentCompany$.next(pricingCompanyResponse);
            });
    }

    public InitAgentInfoFromPublicZone(agentId: string) {
        this.getPublicUserInfo(agentId)
            .pipe(first())
            .subscribe(agentSS => {
                this.SetAgentSS(agentSS, true);
            });
    }

    public fetchAgentSnapshot(agentId: string): Observable<LgvModel.AgentSS> {
        if (this.AgentSS$.value?.agentInfo?.id === agentId) {
            return of(this.AgentSS$.value);
        } else {
            const bldUrl = `${env.LgvApiBaseUrl}/users/${agentId}/snapshot`;
            return this.http.get<LgvModel.AgentSS>(bldUrl);
        }
    }

    public LoadClientAgent() {
        this.http
            .get<LgvModel.AgentSS>(
                `${env.LgvApiBaseUrl}/user/client/agent/snapshot`
            )
            .pipe(first())
            .subscribe(
                x => {
                    // Force the skin to match the agent's company
                    //console.log(`XXX setting company code from user in LoadClientAgent() - ${x.company.companyCode}  XXX`);
                    //console.log(x.company);
                    this.lgvCompanySvc.setActiveCompany(x.company.companyCode);
                    this.DBUserSS$.next(x);
                    this.SetAgentSS(x);
                },
                () => this.AgentState$.next('Error')
            );
    }

    public LoadDBUserSS(firstLoad: boolean = false) {
        this.http
            .get<LgvModel.AgentSS>(`${env.LgvApiBaseUrl}/user/snapshot`)
            .pipe(first())
            .subscribe(
                x => {
                    this.getOnboardingChecklistStatus(x?.agentInfo?.id);

                    this.DBUserSS$.next(x);
                    this.SetAgentSS(x);
                    this.authMgr.UserFirstName$.next(x?.agentInfo?.firstName);

                    if (firstLoad) {
                        // Force the skin to match the agent's company
                        //console.log(`XXXXXX setting company code from user in LoadDBUserSS() - ${x.company.companyCode}  XXXXXX`);
                        //console.log(x.company);
                        this.lgvCompanySvc.setActiveCompany(
                            x.company.companyCode
                        );

                        this.LoadDBUserAssistantBinding(x.agentInfo.id);
                        this.LoadUsers();

                        if (this.isPreLicensedAgent) {
                            this.matDialog.open(SplashDialogComponent);
                        }
                    }
                },
                (err: Error) => {
                    const msg = 'There was an error loading the user info';
                    console.log(msg);
                    this.NotificationSvc.error(msg);
                }
            );
    }

    public DeleteUser(agentID: string): Observable<any> {
        if (!this.IsAdmin) {
            return;
        }

        return this.http
            .delete<LgvModel.AgentSS>(`${env.LgvApiBaseUrl}/users/${agentID}`)
            .pipe(first());
    }

    public getLicensePipeline(userId: string) {
        return this.http
            .get<LicensingPipeline>(
                `${env.LgvApiBaseUrl}/users/${userId}/licensing/pipeline`
            )
            .pipe(first());
    }

    public showPreLicensingModal() {
        const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
            data: {
                header: `Licensing in Progress &nbsp;
                        <fa-icon style="color: #9B9B9B;" [icon]="['far', 'lock-keyhole']"></fa-icon>`,
                Confirmation:
                    'This action or page is currently unavailable to users who are not licensed. Once you are licensed and approved, you’ll have full access to edit/view loan applications and tasks for your borrowers, as well as share pricing with clients.',
                button: 'Go To Licensing'
            },
            width: '500px'
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.action) {
                this.router.navigate(['/admin/licensing']);
            }
        });
    }

    openUnearnedCommissionInfo() {
        const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
            data: {
                header: 'Unearned Commission',
                Confirmation:
                    'Until you are licensed, your unearned commissions are passed through as savings to the borrower. This amount is an estimate of what your commission for this loan would be.',
                button: 'Go To Licensing'
            },
            width: '500px'
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.action) {
                this.router.navigate(['/admin/licensing']);
            }
        });
    }

    //#region Impersonation
    public ImpersonateThisAgentAsAssistant(userID: string) {
        this.SavedAssistantImpersonation = userID;
        this.ImpersonateAgent(userID);
    }

    public StopAgentAssistantImpersonation() {
        this.SavedAssistantImpersonation = null;
        this.StopImpersonatingAgent();
    }

    public LoadAgentSS(agentID) {
        this.AgentState$.next('Loading');
        this.AgentSS$.next(null);
        this.http
            .get<LgvModel.AgentSS>(
                `${env.LgvApiBaseUrl}/users/${agentID}/snapshot`
            )
            .pipe(first())
            .subscribe(x => {
                this.SetAgentSS(x);
            });
    }

    public ImpersonateAgent(agentID: string, force: boolean = false) {
        if (
            !force &&
            this.HaveDBUserSS &&
            this.AgentSS$.value &&
            this.AgentSS$.value.agentInfo.id === agentID
        ) {
            //console.log('*** Skipping Agent Impersonation ***');
            return;
        }

        this.LoadAgentSS(agentID);
    }

    public StopImpersonatingAgent() {
        //console.log('*** Stopping Agent Impersonation ***');
        this.AgentState$.next('Loading');
        this.SetAgentSS(this.DBUserSS$.value);
    }

    //#endregion Impersonation

    //#region Assistant Binding

    public AddAssistantBinding(
        assistantID: string,
        agentID: string,
        assitantGetsEmails: boolean
    ): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/users/${agentID}/assistants`;

        let model = {
            assistantId: assistantID,
            alertWhenEmailSent: assitantGetsEmails
        };

        return this.http.post<any>(bldUrl, model).pipe(first());
    }

    public RemoveAssistantBinding(
        assistantID: string,
        agentID: string
    ): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/users/${agentID}/assistants/${assistantID}`;

        return this.http.delete<any>(bldUrl).pipe(first());
    }

    public FetchAssistantToBinding(
        userID: string
    ): Observable<LgvModel.Assistant[]> {
        return this.http
            .get<LgvModel.Assistant[]>(
                `${env.LgvApiBaseUrl}/users/${userID}/assistant-to`
            )
            .pipe(first());
    }
    public FetchAssistantsBinding(
        userID: string
    ): Observable<LgvModel.Assistant[]> {
        return this.http
            .get<LgvModel.Assistant[]>(
                `${env.LgvApiBaseUrl}/users/${userID}/assistants`
            )
            .pipe(first());
    }
    public FetchAgentInfo(userID: string): Observable<LgvModel.Agent> {
        return this.http
            .get<LgvModel.Agent>(`${env.LgvApiBaseUrl}/users/${userID}`)
            .pipe(first());
    }

    public LoadDBUserAssistantBinding(userID: string) {
        this.FetchAssistantToBinding(userID).subscribe(x => {
            this.DBUserAssistingAgents$.next(x);
        });
        this.FetchAssistantsBinding(userID).subscribe(x => {
            this.DBUserAssistants$.next(x);
        });
    }
    //#endregion Assistant Binding

    //#region Cobranding Agent
    public LoadDBUserCobrandingRealEstatePartners(userID: string) {
        let bldUrl = `${env.LgvApiBaseUrl}/users/${userID}/cobranding/realestate-agents`;
        this.http
            .get<any>(bldUrl)
            .pipe(takeWhile(f => f != null))
            .subscribe(res => {
                //console.log('Setting results for CobrandedRealEstatePartners$');
                //console.log(res.value);
                this.CobrandedRealEstatePartners$.next(res.value);
            });
    }

    public GetCoBrandedRealEstatePartnerByID(id: string): LgvModel.AgentDTO {
        //console.log('GetCoBrandedRealEstatePartnerByID()');
        //console.log(this.CobrandedRealEstatePartners$.value);

        if (!this.CobrandedRealEstatePartners$.value) {
            return null;
        }
        let partner: LgvModel.AgentDTO = this.CobrandedRealEstatePartners$.value.find(
            x => x.agentInfo.id === id
        );
        return partner;
    }

    //#endregion Cobranding Agent

    //region Pricing Preferences
    fetchPricingSettings(
        userId: string
    ): Observable<LgvModel.PricingProviderSettings> {
        let bldUrl = `${env.LgvApiBaseUrl}/users/${userId}/loan-agent/pricing-settings`;
        return this.http.get<any>(bldUrl);
    }

    fetchProviders(
        userId: string,
        providerType: ProviderType
    ): Observable<LgvModel.GenericProviderData[]> {
        let bldUrl = `${env.LgvApiBaseUrl}/users/${userId}/providers?providerType=${providerType}`;
        return this.http.get<any>(bldUrl);
    }

    saveProviders(
        userId: string,
        providerType: ProviderType,
        isNew: boolean,
        model: any
    ): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/users/${userId}/providers?providerType=Pricing`;
        if (isNew) {
            return this.http.post<any>(bldUrl, model);
        } else {
            return this.http.patch<any>(bldUrl, model);
        }
    }

    //endregion Pricing Preferences

    public LoadUserRoles() {
        let bldUrl = `${env.LgvApiBaseUrl}/users/roles`;
        this.http
            .get<LgvModel.Role[]>(bldUrl)
            .pipe(first())
            .subscribe(res => {
                this.roles$.next(res);
            });
    }

    public loadUserAssets(userId: string) {
        this.fetchUserAssets(userId)
            .pipe(first())
            .subscribe(x => {
                this.agentAssets$.next(x);

                /*
                 * agent image is now stored as an image asset.  Inject the url into the old location (agentInfo.profileImageUrl)
                 * Also append a random int to prevent image caching
                 */

                let agentProfileImg = x.find(x => x.name === 'User_Profile');
                if (
                    agentProfileImg &&
                    !!this.AgentSS$.value &&
                    this.AgentSS$.value?.agentInfo?.id === userId
                ) {
                    this.AgentSS$.value.agentInfo.profileImageUrl =
                        agentProfileImg.url + LgvTSHelper.imgSuffix();
                    this.AgentSS$.next(this.AgentSS$.value);
                }
            });
    }

    public GetUserById(userID: string): Observable<LgvModel.Agent> {
        return this.http
            .get<LgvModel.Agent>(`${env.LgvApiBaseUrl}/users/${userID}`)
            .pipe(first());
    }

    public CreateUser(model: any): Observable<any> {
        let bldUrl = env.LgvApiBaseUrl;
        bldUrl += `/users`;
        return this.http.put<any>(bldUrl, model).pipe(first());
    }

    public SearchWebServiceUsersByCompany(
        searchText: string,
        company: string,
        organization
    ): Observable<any> {
        const searchTextParam = !searchText ? '' : `txtSearch=${searchText}`;
        const companyParam = !company ? '' : `&companyId=${company}`;
        const organizationParam = !organization
            ? ''
            : `&organizationId=${organization}`;
        let bldUrl = `${env.LgvApiBaseUrl}/users?${searchTextParam}${companyParam}${organizationParam}`;
        return this.http.get<any>(bldUrl).pipe(first());
    }

    public PasswordReset(id: string) {
        let bldUrl = `${env.LgvApiBaseUrl}/users/${id}/password-reset`;

        return this.http.post<any>(bldUrl, {}).pipe(
            first(),
            tap(t => this.userList$.next(t))
        );
    }

    public static ConvertObjToUserDTO(obj: any): LgvModel.UserDTO {
        return {
            id: obj.id,
            firstName: obj.firstName,
            lastName: obj.lastName,
            email: obj.email,
            nickName: obj.nickName,
            userRoles: obj.userRoles
        };
    }

    public static ConvertAgentDTO(
        agentSS: LgvModel.AgentSS
    ): LgvModel.AgentDTO {
        return {
            agentInfo: agentSS.agentInfo,
            company: agentSS.company,
            organization: agentSS.organization,
            coBrandedAgent: agentSS.coBrandedAgent
        };
    }

    public static ConvertCobrandedAgentDTO(
        agentSS: LgvModel.AgentSS
    ): LgvModel.AgentDTO {
        if (agentSS.coBrandedAgent === null) {
            return null;
        }

        return {
            agentInfo: agentSS.coBrandedAgent.agentInfo,
            company: agentSS.coBrandedAgent.company,
            organization: agentSS.coBrandedAgent.organization
        };
    }

    public SaveCoBrandingAgent(
        coBrandedAgent: string,
        agentID: string
    ): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/users/${agentID}/cobranding/loan-agent`;

        let model = {
            agentId: coBrandedAgent
        };

        return this.http.patch<any>(bldUrl, model).pipe(first());
    }

    public FetchCoBrandingAgent(
        level: string = null,
        agentID: string
    ): Observable<any> {
        let param = level != null ? `?level=${level}` : '';
        let bldUrl = `${env.LgvApiBaseUrl}/users/${agentID}/cobranding/loan-agent${param}`;
        return this.http.get<any>(bldUrl).pipe(first());
    }
    public LoadLoanPurposeType() {
        let bldUrl = `${env.LgvApiBaseUrl}/types/loanpurpose`;
        return this.http.get<any>(bldUrl).pipe(first());
    }
    public LoadLoanType() {
        let bldUrl = `${env.LgvApiBaseUrl}/types/loan`;
        return this.http.get<any>(bldUrl).pipe(first());
    }
    public LoadLienType() {
        let bldUrl = `${env.LgvApiBaseUrl}/types/lien`;
        return this.http.get<any>(bldUrl).pipe(first());
    }

    public FetchCoBrandingDTOAgentFromCache(
        searchID: string
    ): Observable<LgvModel.AgentDTO> {
        //    get Agent$(): Observable<LgvModel.Agent> { return this.AgentSS$.pipe(map(x => { return x === null ? null : x.agentInfo })) }  // Phasing Agent$ to AgentSS$

        return this.CobrandedRealEstatePartners$.pipe(
            first(f => f !== null),
            map(x => x.filter(f => f.agentInfo.id === searchID)),
            map(x => (x && x.length === 1 ? x[0] : null))
        );
    }

    public postAssetFile(userId: string, data: FormData): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/users/${userId}/assets`;
        return this.http.post<any>(bldUrl, data);
    }

    public fetchUserAssets(
        userId: string
    ): Observable<LgvModel.BrandingAsset[]> {
        let bldUrl = `${env.LgvApiBaseUrl}/users/${userId}/assets`;

        return this.http.get<LgvModel.BrandingAsset[]>(bldUrl).pipe(first());
    }

    public deleteAssetFile(userId: string, assetName: string): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/users/${userId}/assets/${assetName}`;
        return this.http.delete<any>(bldUrl);
    }

    public getThisBrandingAsset$(name: string): Observable<string> {
        //console.log('Looking for ' + name);

        return this.agentAssets$.pipe(
            map(assets => {
                if (!!assets == false) {
                    //console.log('returning null');
                    return null;
                }
                let asset = assets.find(x => x.name === name);

                let suffix = this.authMgr.IsClient
                    ? ''
                    : LgvTSHelper.imgSuffix();
                return !!asset ? asset.url + suffix : null;
            })
        );
    }

    //#region Mock Data
    public DummyCompany(): LgvModel.CompanyInfo {
        return {
            id: 'xxx',
            businessTypeId: 'xxx',
            businessTypeName: 'xxx',
            name: 'xxx',
            dbaname: 'xxx',
            address1: 'xxx',
            address2: 'xxx',
            city: 'xxx',
            state: 'xxx',
            zipCode: 'xxx',
            phone: 'xxx',
            fax: 'xxx',
            website: 'xxx',
            nmlsid: 'xxx',
            licenseNumber: 'xxx',
            companyLogoUrl: 'xxx',
            companyEmail: 'xxx',
            disclaimer: 'xxx',
            isDisabled: false,
            externalId: 'xxx',
            createdTimestamp: null,
            updatedTimestamp: 'xxx',
            companyCode: 'xxx'
        };
    }

    loanAppCompany: LgvModel.ApplicationCompany = null;

    public DummyOrganization(): LgvModel.Organization {
        return {
            id: 'xxx',
            name: 'xxx',
            address1: 'xxx',
            address2: 'xxx',
            city: 'xxx',
            state: 'xxx',
            zipCode: 'xxx',
            phone: 'xxx',
            fax: 'xxx',
            nmlsid: 'xxx',
            licenseNumber: 'xxx',
            organizationLogoUrl: 'xxx',
            organizationEmail: 'xxx',
            disclaimer: 'xxx',
            companyId: 'xxx',
            isDisabled: false,
            externalId: 'xxx',
            createdTimestamp: null,
            updatedTimestamp: 'xxx',
            applications: []
        };
    }

    public DummyAgentInfo(): LgvModel.Agent_Basic {
        return {
            id: '8948c658-6b20-eb11-96f5-00155de21c75',
            firstName: 'Larry',
            lastName: 'LoanOfficer',
            username: 'larryLO',
            nickName: 'xxx',
            jobTitle: 'Lender Extraordinaire',
            companyId: 'xxx',
            email: 'homedashboard+LarryLO@realfinity.io',
            address1: '432 Test Ave',
            address2: null,
            city: 'TestCity',
            state: 'CA',
            zipCode: '94801',
            phone: '9257771111',
            mobilePhone: '9252225151',
            fax: '7776403335',
            organizationId: '578ff331-5af9-ec11-b479-a085fc8e388c',
            website: 'xxx',
            profileImageUrl:
                'https://rftylusstrblb01.blob.core.windows.net/agent-profile-images/8948C658-6B20-EB11-96F5-00155DE21C75.jpg',
            facebook: 'xxx',
            twitter: 'xxx',
            google: 'xxx',
            linkedin: 'xxx',
            youtube: 'xxx',
            disclaimer: 'xxx',
            yelp: 'xxx',
            instagram: 'xxx',
            zillow: 'xxx',
            applicationUrl: 'xxx',
            isReadyForPricing: false
        };
    }

    public DummyAgentDTO$(): Observable<LgvModel.AgentDTO> {
        return of({
            agentInfo: this.DummyAgentInfo(),
            company: this.DummyCompany(),
            organization: this.DummyOrganization()
        });
    }
    //#endregion Mock Data

    public getEmailStatus(agentId) {
        const url = `${env.LgvApiBaseUrl}/users/${agentId}/email/sender`;
        return this.http.get<any>(url).pipe(
            first(),
            catchError(e => {
                return of(null);
            })
        );
    }

    public sendVerificationEmail(agentId, requestBody) {
        const url = `${env.LgvApiBaseUrl}/users/${agentId}/email/sender`;
        return this.http.post<any>(url, requestBody).pipe(first());
    }

    public resendVerificationEmail(agentId, requestBody) {
        const url = `${env.LgvApiBaseUrl}/users/${agentId}/email/sender/resend-verification`;
        return this.http.post<any>(url, requestBody).pipe(first());
    }

    public getOnboardingChecklist(agentId) {
        const url = `${env.LgvApiBaseUrl}/users/${agentId}/onboarding-checklist`;
        return this.http.get<any>(url).pipe(
            first(),
            catchError(e => {
                return null;
            })
        );
    }

    public getOnboardingChecklistStatus(agentId) {
        const url = `${env.LgvApiBaseUrl}/users/${agentId}/onboarding-checklist`;
        this.http
            .get<any>(url)
            .pipe(
                first(),
                catchError(e => {
                    return null;
                })
            )
            .subscribe(checklist => {
                this.refreshOnboardingChecklistStatus(checklist);
            });
    }

    public refreshOnboardingChecklistStatus(checklist) {
        const isOnboarding = !(
            checklist?.hasCompleteAgentProfile &&
            checklist?.hasSendEmailAsPermission
        );
        const isREAgent =
            this.AgentSS$?.value?.company?.businessTypeName ===
            BusinessTypeName.RealEstate;

        const newIsOnboarding = isOnboarding && isREAgent;

        if (this.isOnboarding$.value !== newIsOnboarding) {
            this.isOnboarding$.next(newIsOnboarding);
        }
    }

    public getInvite(agentId) {
        const url = `${env.LgvApiBaseUrl}/subscribe/invite/${agentId}`;
        return this.http.get<any>(url).pipe(
            first(),
            map(() => of(true)),
            catchError(e => {
                return of(false);
            })
        );
    }

    public startBillingPortalSession(agentId, type) {
        const model = {
            id: agentId
        };

        const bldUrl = `${env.LgvApiBaseUrl}/users/${agentId}/billing/${type}`;

        return this.http.post<any>(bldUrl, model);
    }

    public createBillingAccount(agent, company, agentId) {
        if (!agent || !company) {
            return;
        }

        const model = {
            customer: {
                firstName: agent.firstName,
                lastName: agent.lastName,
                company: company.name,
                phone: agent.phone,
                email: agent.email
            }
        };

        const bldUrl = `${env.LgvApiBaseUrl}/users/${agentId}/billing/customer`;

        return this.http.post<any>(bldUrl, model);
    }

    public updateBillingStatus(userId) {
        return this.http.get<any>(
            `${env.LgvApiBaseUrl}/users/${userId}/billing/customer`
        );
    }

    public getPublicUserInfo(userId): Observable<LgvModel.AgentSS> {
        const bldUrl = `${env.LgvApiBaseUrl}/public/users/${userId}/snapshot`;
        return this.http.get<LgvModel.AgentSS>(bldUrl);
    }

    // for public use only (quick pricer, apply now, etc...)
    public startLoanApplication(
        agentId: string,
        model: LgvLoanApp.LoanApplicationDetail
    ) {
        const url = `${env.LgvApiBaseUrl}/public/users/${agentId}/loanapplications`;
        return this.http.post<any>(url, model).pipe(first());
    }

    public static isApplicationCompany(
        appCompany: LgvModel.ApplicationCompany
    ) {
        return (
            !!appCompany &&
            (appCompany.name.toUpperCase() === 'FLOIFY' ||
                appCompany.name.toUpperCase() === 'BLEND' ||
                appCompany.name.toUpperCase() === 'LENDINGPAD' ||
                appCompany.name.toUpperCase() === 'MERIDIANLINK')
        );
    }

    public getUserFloifyToken(userId): Observable<LgvModel.NameValueDTO> {
        const bldUrl = `${env.LgvApiBaseUrl}/users/${userId}/tokens/floify-user-token`;
        return this.http.get<LgvModel.NameValueDTO>(bldUrl);
    }

    public saveLongUrl(url: string) {
        const bldUrl = `${env.LgvApiBaseUrl}/longurl`;
        return this.http.post<any>(bldUrl, {
            longUrl: url
        });
    }

    public getLongUrl(shortCode: string) {
        const bldUrl = `${env.LgvApiBaseUrl}/longurl/${shortCode}`;
        return this.http.get<any>(bldUrl);
    }

    public getLoanApplications(query) {
        const bldUrl = `${env.LgvApiBaseUrl}/loanapplications/${query}`.replace(
            /[$]/gm,
            ''
        );
        return this.http.get<any>(bldUrl);
    }

    public getLoanApplicationsSnapshot() {
        const bldUrl = `${env.LgvApiBaseUrl}/loanapplications/snapshot`;
        return this.http.get<any>(bldUrl);
    }

    public getLoanApplicationsForClient(
        clientId: string,
        includeDeleted: boolean = false
    ): Observable<any> {
        const bldUrl = `${env.LgvApiBaseUrl}/loanapplications/?clientId=${clientId}&includeDelete=${includeDeleted}`;
        return this.http.get<any>(bldUrl);
    }

    // Method to handle new email notifications from any source
    public handleNewEmail(email: any) {
        this.onNewEmail.emit(email);
        this.getFolderSummary();
    }
}
